/** @jsx jsx */

import { Component, ChangeEvent } from 'react';

import {
  Button,
  Field,
  Grid,
  IconButton,
  TextInput,
  Tooltip,
} from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import { quickCopy } from 'app/developer-v3/utils';

const Styles = {
  root: ({ isDisabled }) => [
    isDisabled &&
      css`
        user-select: none;
        opacity: 0.7;
        cursor: default;
      `,
  ],
};

export type InputValue = {
  error?: string;
  isInvalidValue?: boolean;
  value: string;
};

type Props = {
  canChangeItemCount: boolean;
  enableAutoComplete: boolean;
  isDisabled: boolean;
  items: InputValue[];
  inputName: string;
  onUpdate: (options: InputValue[]) => void;
};

type State = {
  indexRequestedDeletion: number | undefined;
};

const getDefaultItem = (): InputValue => ({
  value: '',
});

let deleteTimeout = null;

class InputEditor extends Component<Props, State> {
  static defaultProps = {
    canChangeItemCount: true,
    enableAutoComplete: false,
    isDisabled: false,
  };

  constructor(props: Props) {
    super(props);

    this.state = { indexRequestedDeletion: -1 };
  }

  onClickAdd = () => {
    this.props.onUpdate([...this.props.items, getDefaultItem()]);
  };

  onChangeInput = (index: number) => (value: string) => {
    const newItems = quickCopy(this.props.items);
    newItems[index].value = value;
    this.props.onUpdate(newItems);
  };

  onClickDelete = (index: number) => {
    const { indexRequestedDeletion } = this.state;

    if (indexRequestedDeletion === index) {
      const newItems = [...this.props.items];
      newItems.splice(index, 1);
      this.props.onUpdate(newItems);

      this.setState({
        indexRequestedDeletion: -1,
      });
    } else {
      this.setState({
        indexRequestedDeletion: index,
      });

      deleteTimeout = setTimeout(() => {
        this.setState({
          indexRequestedDeletion: -1,
        });
      }, 3000);
    }
  };

  removeEmptyItems = () => {
    if (deleteTimeout) {
      clearTimeout(deleteTimeout);
    }

    const nonEmptyItems = this.props.items.filter(item => item.value);
    this.props.onUpdate(nonEmptyItems);
  };

  getCommonInputProps = (
    item: InputValue,
    index: number,
    inputName: string,
    isDisabled: boolean
  ) => ({
    placeholder: inputName,
    name: inputName,
    value: item.value,
    onChange: (event: ChangeEvent<HTMLInputElement>) =>
      this.onChangeInput(index)(event.target.value),
    onChangeValue: this.onChangeInput(index),
    isReadonly: isDisabled,
    fauxReadOnly: isDisabled,
  });

  render() {
    const { canChangeItemCount, inputName, isDisabled, items } = this.props;
    const { indexRequestedDeletion } = this.state;

    return (
      <div css={Styles.root({ isDisabled })}>
        <Grid columns={1} gap={20}>
          {items.map((item, index) => (
            <Field
              error={item.error}
              key={index}
              label=""
              renderInput={() => (
                <Grid
                  columns={`repeat(1, ${
                    canChangeItemCount ? '1fr 45px' : '1fr'
                  })`}
                  gap={15}
                >
                  <TextInput
                    {...this.getCommonInputProps(
                      item,
                      index,
                      inputName,
                      isDisabled
                    )}
                    aria-label={inputName}
                    isErrored={item.isInvalidValue}
                  />
                  {canChangeItemCount && (
                    <Tooltip content="Remove item" placement="left">
                      <IconButton
                        aria-label="Remove item"
                        name={
                          indexRequestedDeletion === index
                            ? 'actionTrashFill'
                            : 'formX'
                        }
                        onPress={this.onClickDelete.bind(null, index)}
                        size="small"
                        variant={
                          indexRequestedDeletion === index
                            ? 'primary'
                            : 'secondary'
                        }
                      />
                    </Tooltip>
                  )}
                </Grid>
              )}
            />
          ))}
          {canChangeItemCount && (
            <Grid columns="repeat(8,1fr)">
              <Button
                isDisabled={isDisabled}
                onPress={this.onClickAdd}
                type="button"
                variant="secondary"
              >
                Add
              </Button>
            </Grid>
          )}
        </Grid>
      </div>
    );
  }
}

export default InputEditor;
